import React, { PropsWithChildren } from 'react';
import { CallToAction } from '../../../commonTypes/links';
import componentProps, { ComponentProps } from '../../../helpers/componentProps';
import { applyComponentTheme } from '../../../theme';
import fluidGridColumns from '../../../theme/fluidGridColumns';
import { CardColumnSpanCalculator } from '../../CardGrid';
import { SecondaryCta } from '../../Cta';
import { Grid, GridItem } from '../../Grid';
import SolidBackground from '../../SolidBackground';
import { CtaContainer, Heading, Intro, NoBackground, StyledCardGrid } from './styles';

type InfoCardGridProps = {
  alignLeft?: boolean;
  displayBackground?: boolean;
  heading?: string;
  intro?: string;
  secondaryCta?: CallToAction;
  columnSpanCalculator?: CardColumnSpanCalculator;
} & ComponentProps;

const getDesktopColumnSpan = (index: number, totalCards: number): number => {
  const colSpanForSingleItemRow = 8;
  const colSpanForTwoItemRow = 5;
  const colSpanForThreeItemRow = 4;

  if (totalCards === 1) {
    return colSpanForSingleItemRow;
  }

  const totalCardsModuloThree = totalCards % 3;
  switch (totalCardsModuloThree) {
    case 0:
      // All rows are rows of three.
      return colSpanForThreeItemRow;
    case 1:
      // All rows except the last two are rows of three, the final two are rows of two.
      return index < totalCards - 4 ? colSpanForThreeItemRow : colSpanForTwoItemRow;
    case 2:
      // All rows except the last are rows of three, the final one is a row of two.
      return index < totalCards - 2 ? colSpanForThreeItemRow : colSpanForTwoItemRow;
    default:
      throw new Error(
        `Expected remainder of less than 3, but found ${totalCardsModuloThree}. Check implementation!`
      );
  }
};

export const columnSpanCalculator: CardColumnSpanCalculator = (
  index: number,
  totalCards: number
) => ({
  // mobile cards are always full-width
  tabletPortrait:
    totalCards % 2 === 1 && index === 0
      ? fluidGridColumns.tabletPortrait
      : fluidGridColumns.tabletPortrait / 2,
  tabletLandscape: getDesktopColumnSpan(index, totalCards),
  desktop: getDesktopColumnSpan(index, totalCards),
});

const InfoCardGrid: React.FC<PropsWithChildren<InfoCardGridProps>> = ({
  children,
  alignLeft,
  displayBackground,
  heading,
  intro,
  secondaryCta,
  columnSpanCalculator: cardColumnSpanCalculator,
  ...props
}) => {
  const Background = displayBackground ? SolidBackground : NoBackground;
  const displayHeader = heading || intro;

  return (
    <Background {...componentProps(props)}>
      {displayHeader && (
        <Grid alignLeft>
          <GridItem focusable>
            {heading && <Heading>{heading}</Heading>}
            {intro && <Intro>{intro}</Intro>}
          </GridItem>
        </Grid>
      )}
      <StyledCardGrid
        alignLeft={alignLeft}
        columnSpanCalculator={cardColumnSpanCalculator ?? columnSpanCalculator}>
        {children}
      </StyledCardGrid>
      {secondaryCta && (
        <CtaContainer>
          <SecondaryCta cta={secondaryCta} arrowDirection="down" />
        </CtaContainer>
      )}
    </Background>
  );
};

export default applyComponentTheme(InfoCardGrid, 'InfoCardGrid');
